import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Printer, MessageCircle, ArrowLeft, Download, Send, BellOff, Loader2, Pencil } from "lucide-react";
import { Toaster, toast } from "sonner";
import ReferralDialog from "../components/ReferralDialog";
import { CLINIC, LogoTile, doctorDisplay, resolveDoctor, absUrl, rxLegalDisclaimer, BrandAccentStyle, usePrintTitle, verifyRxUrl, withLang } from "../lib/clinic";
import { openWhatsApp } from "../lib/whatsapp";
import { tr } from "../lib/i18n";
import { getEducationText } from "../lib/patientEducation";
import { patientLanguageName, PATIENT_LANGUAGES, languageDirection } from "../lib/patientLanguages";
import { PatientLanguageSelect } from "../components/PatientLanguageSelect";
import { usePrintAdviceTranslation } from "../hooks/usePrintAdviceTranslation";
import "./PrintPrescription.css";
import { activeFhInsights } from "../lib/familyHistory";
import { QRCodeSVG } from "qrcode.react";
import { doseDisplay, productStrengthLabel, stripMarkdown, isCombinedProductName } from "../lib/doseCompat"; // MT-11B.7 / B.8

export default function PrintPrescription() {
  const { rxId } = useParams();
  const nav = useNavigate();
  const [rx, setRx] = useState(null);
  const [patient, setPatient] = useState(null);
  const [visit, setVisit] = useState(null);
  // MT-11B.7: ONE canonical follow-up date for print (also used by reminders +
  // calendar) — the Rx mirrors the visit's follow_up_date; older Rx fall back
  // to the visit's. Never re-derived from the AI suggestion.
  const canonicalFollowUpIso = rx?.follow_up_date || visit?.follow_up_date || null;
  const canonicalFollowUpLabel = canonicalFollowUpIso
    ? new Date(canonicalFollowUpIso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
    : "";
  const [lang, setLang] = useState("en");
  // MT-11C.4: PDF Title metadata = active tenant clinic + the SAME signer shown
  // in the signature block (server-set signed_by_doctor_id → tenant profile).
  usePrintTitle(rx ? doctorDisplay(resolveDoctor(rx.signed_by_doctor_id).name) : "", rx?.prescription_id || "");
  const [customDict, setCustomDict] = useState({});
  // Doctor-controlled toggles for what shows on the printed prescription.
  // Titration / instruction lines are hidden by default — patients rarely
  // need to see them and they clutter the take-home Rx.
  // Default ON so Marathi instructions from My List print without an extra tap;
  // remembers the doctor's last choice.
  const [printTitrate, setPrintTitrate] = useState(() => localStorage.getItem("rx-print-titrate") !== "0");
  // Estimated pharmacy bill from the clinic's own price list (my_drug_list)
  const [printCost, setPrintCost] = useState(() => localStorage.getItem("rx-print-cost") !== "0");
  const [costEst, setCostEst] = useState(null);
  // Care-kit QR — a 30-day patient-portal link printed on the Rx footer so
  // patients can reopen their full prescription + education kits at home.
  const [printKitQr, setPrintKitQr] = useState(() => localStorage.getItem("rx-print-kitqr") !== "0");
  const [careLink, setCareLink] = useState("");
  // Per-vaccine-row "cancelling" state — locks the Cancel button while the
  // DELETE request is in flight so the doctor can't double-fire it.
  const [cancellingRecall, setCancellingRecall] = useState({});
  // Local override of the visit's `vaccine_calendar_events` list so the UI
  // updates instantly when a recall is cancelled without a full re-fetch.
  const [recallEvents, setRecallEvents] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/translations");
        const map = {};
        (r.data.translations || []).forEach((t) => { map[t.english] = { mr: t.mr, hi: t.hi }; });
        setCustomDict(map);
      } catch (e) { console.debug("[silent]", e); }
    })();
  }, []);

  const adviceText = (visit?.advice || rx?.advice || "").trim();
  const adviceTranslation = usePrintAdviceTranslation(rxId, lang, adviceText);
  const hasPrintDictionary = ["en", "mr", "hi"].includes(lang);

  useEffect(() => {
    let active = true;
    setRx(null); setPatient(null); setVisit(null); setLang("en"); setCareLink(""); setCostEst(null);
    (async () => {
      try {
        const r = await api.get(`/prescriptions/${rxId}`);
        const [p, v] = await Promise.all([
          api.get(`/patients/${r.data.patient_id}`),
          api.get(`/visits/${r.data.visit_id}`),
        ]);
        if (!active) return;
        const savedLanguage = p.data.patient.preferred_language;
        setLang(PATIENT_LANGUAGES.some((l) => l.code === savedLanguage) ? savedLanguage : "en");
        setPatient(p.data.patient);
        setVisit(v.data.visit);
        setRx(r.data);
        setRecallEvents(v.data.visit?.vaccine_calendar_events || []);
      } catch { if (active) toast.error("Failed to load prescription"); }
    })();
    return () => { active = false; };
  }, [rxId]);

  // Estimated pharmacy bill from the clinic's own price list
  useEffect(() => {
    if (!rx?.medicines?.length) return;
    api.post("/mydrugs/estimate-cost", { medicines: rx.medicines })
      .then((r) => setCostEst(r.data))
      .catch((e) => console.debug("[silent]", e));
  }, [rx]);

  // Care-kit QR link — created once per page load, only when the toggle is on
  useEffect(() => {
    if (!rx || !printKitQr || careLink) return;
    api.post("/portal/link", { patient_id: rx.patient_id, expires_hours: 24 * 30, reuse: true })
      .then((r) => setCareLink(r.data?.portal_url || ""))
      .catch((e) => console.debug("[silent]", e));
  }, [rx, printKitQr, careLink]);

  // Given a vaccine row, is a Calendar recall currently scheduled? Matches by
  // both name AND date so re-adding a cancelled row correctly re-enables the
  // Cancel-recall button after a fresh save.
  const hasRecall = (v) => recallEvents.some(
    (e) => e && e.vaccine === v.name && String(e.date) === String(v.date)
  );

  // Fire the delete endpoint, drop the row from local state, and toast.
  const cancelRecall = async (v) => {
    const key = `${v.name}__${v.date}`;
    if (cancellingRecall[key]) return;
    setCancellingRecall((s) => ({ ...s, [key]: true }));
    try {
      await api.delete(`/visits/${rx.visit_id}/vaccine-recall`, {
        params: { vaccine: v.name, date: v.date },
      });
      setRecallEvents((prev) => prev.filter(
        (e) => !(e.vaccine === v.name && String(e.date) === String(v.date))
      ));
      toast.success(`Cancelled calendar recall for ${v.name}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to cancel recall");
    } finally {
      setCancellingRecall((s) => { const n = { ...s }; delete n[key]; return n; });
    }
  };

  // Single-row WhatsApp nudge — pre-fills a short recall text so the doctor
  // can send the patient a personalised reminder in one tap.
  const sendVaccineWhatsApp = (v) => {
    const dt = v.date
      ? new Date(v.date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
      : "";
    const msg =
`Dear ${patient?.name},
This is a friendly reminder from ${CLINIC.name}.
Your *${v.name}* vaccine is scheduled on *${dt}*.
Please arrive 10 minutes early. Reply here if you need to reschedule.
— ${doctorDisplay(CLINIC.doctorName)}`;
    openWhatsApp(patient?.mobile, msg);
    // Fire-and-forget analytics ping — never blocks the WhatsApp opener.
    api.post("/vaccine-recalls/track", {
      patient_id: rx.patient_id,
      visit_id: rx.visit_id,
      vaccine: v.name,
      date: v.date,
    }).catch(() => { /* swallow — tracking must never disrupt UX */ });
  };

  const share = async () => {
    // We used to attach the public /verify-rx/{token} link here — that page
    // hides doses/timing/duration so pharmacies can spot forgeries without
    // seeing PHI. That link is WRONG for the patient themselves; they need
    // the FULL prescription. The patient-portal path (already built with an
    // OTP wall) delivers exactly that: last 4 digits of the patient's mobile
    // unlocks the full Rx + reports on their phone, and expires in 24 h.
    let patientLink = "";
    let expiryLine = "";
    try {
      const r = await api.post("/portal/link", { patient_id: rx.patient_id });
      const base = r.data?.portal_url || "";
      // Append the print language so the patient sees the disclaimer in
      // their own tongue on the portal. English is the default.
      patientLink = withLang(base, lang);
      expiryLine = "\nEnter the last 4 digits of this mobile as OTP. Link expires in 24 hours.";
    } catch (e) {
      // Portal creation failed — fall back to the redacted verify link so at
      // least the patient sees SOMETHING and can call the clinic. This path
      // is intentionally silent; the doctor will notice missing portal_url.
      console.debug("[silent] portal link fallback", e?.message || e);
      patientLink = rx.verify_token ? verifyRxUrl(rx) : "";
    }
    // Pull vaccines from the visit first (fresher), fall back to the Rx snapshot.
    const vaccines = (visit?.vaccines_planned || rx?.vaccines_planned || []).filter(
      (v) => v && (v.name || v.date)
    );
    const vaccinesBlock = vaccines.length
      ? `\n\n*Vaccines scheduled:*\n${vaccines
          .map((v) => {
            const dt = v.date
              ? new Date(v.date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })
              : "—";
            return `• ${v.name || "Vaccine"} — ${dt}`;
          })
          .join("\n")}`
      : "";
    const msg =
`Dear ${patient?.name},
Prescription from ${CLINIC.name} dated ${new Date(rx.created_at).toLocaleDateString()}.
Follow-up: ${rx.follow_up || "-"}${canonicalFollowUpLabel ? ` (${canonicalFollowUpLabel})` : ""}.${patientLink ? `

View your prescription:
${patientLink}${expiryLine}` : ""}${vaccinesBlock}
Please contact the clinic if you have any questions.`;
    openWhatsApp(patient?.mobile, msg);
  };

  if (!rx || !patient) return <div className="p-10 text-slate-500">Loading…</div>;

  // PHASE-B1: an unconfigured (fresh-clone) instance must not print prescriptions
  if (CLINIC.configured === false) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-8">
      <div className="max-w-md text-center border border-amber-300 bg-amber-50 rounded-lg p-6 text-amber-900" data-testid="clinic-unconfigured-block">
        <div className="font-heading font-semibold mb-1">Clinic Profile incomplete</div>
        <div className="text-sm">Complete and verify Clinic Profile before issuing prescriptions.</div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-100 print-shell prescription-preview">
      <Toaster position="top-right" richColors />
      <div data-testid="prescription-print-toolbar" className="no-print bg-white border-b border-slate-200 px-4 sm:px-6 py-3 flex flex-wrap items-center justify-between gap-3">
        <button onClick={() => nav(-1)} className="p-1.5 rounded hover:bg-slate-100 flex items-center gap-2 text-sm" data-testid="print-back"><ArrowLeft size={14} /> Back</button>
        <div className="flex flex-wrap items-center gap-2 min-w-0 max-w-full">
          <div className="print-language-control mr-2" data-testid="print-language-control">
            <PatientLanguageSelect value={lang} onChange={setLang} testId="print-language-select" label="Prescription language" />
          </div>
          <Button onClick={() => window.print()} disabled={adviceTranslation.loading} data-testid="print-btn" className="bg-slate-900 hover:bg-slate-800 text-white gap-2"><Printer size={14} /> Print / Save PDF</Button>
          <Button
            onClick={() => nav(`/patient/${rx.patient_id}?edit=${rx.visit_id}`)}
            variant="outline"
            className="gap-2 border-amber-300 text-amber-700 hover:bg-amber-50"
            data-testid="edit-rx-btn"
            title="Reopen this visit with all vitals, labs and medicines loaded — saving updates this same prescription"
          >
            <Pencil size={14} /> Edit
          </Button>
          <label className="flex items-center gap-1.5 text-[11px] text-slate-700 cursor-pointer select-none" data-testid="print-titrate-toggle-label">
            <input
              type="checkbox"
              checked={printTitrate}
              onChange={(e) => { setPrintTitrate(e.target.checked); localStorage.setItem("rx-print-titrate", e.target.checked ? "1" : "0"); }}
              className="w-3.5 h-3.5 accent-pink-600 cursor-pointer"
              data-testid="print-titrate-toggle"
            />
            Print titrate & instructions
          </label>
          <label className="flex items-center gap-1.5 text-[11px] text-slate-700 cursor-pointer select-none" data-testid="print-cost-toggle-label">
            <input
              type="checkbox"
              checked={printCost}
              onChange={(e) => { setPrintCost(e.target.checked); localStorage.setItem("rx-print-cost", e.target.checked ? "1" : "0"); }}
              className="w-3.5 h-3.5 accent-emerald-600 cursor-pointer"
              data-testid="print-cost-toggle"
            />
            Print cost estimate
          </label>
          <label className="flex items-center gap-1.5 text-[11px] text-slate-700 cursor-pointer select-none" data-testid="print-kitqr-toggle-label">
            <input
              type="checkbox"
              checked={printKitQr}
              onChange={(e) => { setPrintKitQr(e.target.checked); localStorage.setItem("rx-print-kitqr", e.target.checked ? "1" : "0"); }}
              className="w-3.5 h-3.5 accent-pink-600 cursor-pointer"
              data-testid="print-kitqr-toggle"
            />
            Print care-kit QR
          </label>
          <Button onClick={share} data-testid="whatsapp-btn" className="bg-emerald-600 hover:bg-emerald-700 text-white gap-2"><MessageCircle size={14} /> WhatsApp</Button>
          <ReferralDialog
            patientId={rx.patient_id}
            visitId={rx.visit_id}
            defaultSummary={[
              rx.diagnosis?.primary && `Diagnosis: ${rx.diagnosis.primary}`,
              visit?.chief_complaints && `Chief complaints: ${visit.chief_complaints}`,
              visit?.known_case_of?.length && `Known case of: ${visit.known_case_of.join(", ")}`,
              visit?.vitals && `Vitals: BP ${visit.vitals.sbp || "-"}/${visit.vitals.dbp || "-"}, BMI ${visit.vitals.bmi || "-"}, RBS ${visit.vitals.random_sugar || "-"}`,
              (rx.medicines || []).length && `Current Rx: ${rx.medicines.map((m) => `${m.brand_name} ${doseDisplay(m) || m.strength || ""} ${m.frequency}`).join("; ")}`,
            ].filter(Boolean).join("\n")}
            trigger={
              <Button variant="outline" data-testid="print-referral-btn" className="gap-2 border-slate-300"><Send size={14} /> Referral</Button>
            }
          />
        </div>
      </div>
      {!hasPrintDictionary && <div role="status" data-testid="print-language-coverage" className="no-print px-4 sm:px-6 py-2 text-xs text-amber-900 bg-amber-50 border-b border-amber-200">
        Medicine directions and the legal notice are not yet translated into {patientLanguageName(lang)}; the originals are retained. {adviceText ? "Advice translation is shown separately when available." : "No advice is recorded for translation."}
      </div>}
      {adviceTranslation.loading && <div role="status" data-testid="print-language-loading" className="no-print px-4 sm:px-6 py-2 text-xs text-slate-600">Translating advice into {patientLanguageName(lang)}…</div>}
      {adviceTranslation.error && <div role="alert" data-testid="print-language-error" className="no-print px-4 sm:px-6 py-2 text-xs text-red-700">
        {adviceTranslation.error} <button onClick={adviceTranslation.retry} data-testid="print-language-retry" className="ml-2 underline font-medium">Retry</button>
      </div>}

      <BrandAccentStyle scope="brand-scope" />
      <div className="max-w-[820px] mx-auto my-6 bg-white border border-slate-200 shadow-sm print-page brand-scope" style={{ WebkitPrintColorAdjust: "exact", printColorAdjust: "exact" }}>
        <div className="p-8">
          {(() => {
            // Resolve the doctor whose signature/name goes on this Rx.
            // Multi-doctor clinics: uses prescription.signed_by_doctor_id if
            // set, else the first doctor in CLINIC.doctors. Single-doctor
            // clinics: falls back to the legacy CLINIC.doctorName block.
            const doc = resolveDoctor(rx?.signed_by_doctor_id);
            const style = CLINIC.print_header_style || "detailed";
            const contact = (
              <div className="text-right text-[10px] text-slate-600 max-w-[220px] leading-snug">
                <div>{CLINIC.address}</div>
                {(CLINIC.phones || []).length > 0 && (
                  <div className="mt-1 tabular">{CLINIC.phones.join(" · ")}</div>
                )}
                {CLINIC.email && <div>{CLINIC.email}</div>}
              </div>
            );

            if (style === "logo-only") {
              // Clinics with pre-printed letterhead — just the logo, no text.
              return (
                <header className="flex justify-center items-center pb-4 border-b-2 border-slate-900" data-testid="print-header-logo-only">
                  <div className="w-24 h-24">
                    <LogoTile className="w-full h-full" padding="p-3" />
                  </div>
                </header>
              );
            }

            if (style === "compact") {
              // Logo + clinic name in one strong row; doctor block on the right
              // for clinics with a long address.
              return (
                <header className="flex justify-between items-center pb-4 border-b-2 border-slate-900" data-testid="print-header-compact">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12"><LogoTile className="w-full h-full" padding="p-1.5" /></div>
                    <div>
                      <div className="font-heading text-xl font-bold text-slate-900 leading-tight">{CLINIC.name}</div>
                      {(CLINIC.phones || []).length > 0 && (
                        <div className="text-[10px] text-slate-600 tabular">{CLINIC.phones.slice(0, 2).join(" · ")}</div>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-slate-800 font-medium">{doctorDisplay(doc.name)}</div>
                    <div className="text-[10px] text-slate-500 leading-snug max-w-[220px]">{doc.qualifications}</div>
                    <div className="text-[10px] text-slate-500">{doc.regNo}</div>
                  </div>
                </header>
              );
            }

            // "detailed" — the default rich header
            return (
              <header className="flex justify-between items-start pb-4 border-b-2 border-slate-900" data-testid="print-header-detailed">
                <div className="flex gap-4 items-start">
                  <div className="w-16 h-20 flex-shrink-0">
                    {CLINIC.logo_url
                      ? <img src={absUrl(CLINIC.logo_url)} alt={CLINIC.name} className="w-full h-full object-contain" />
                      : require("../lib/clinic").LOGO_SVG}
                  </div>
                  <div>
                    <div className="font-heading text-xl font-bold text-slate-900 leading-tight">{CLINIC.name}</div>
                    <div className="text-xs text-slate-700 mt-1 font-medium">{doctorDisplay(doc.name)}</div>
                    <div className="text-[10px] text-slate-500 leading-snug">{doc.qualifications}</div>
                    <div className="text-[10px] text-slate-500">{doc.regNo}</div>
                  </div>
                </div>
                {contact}
              </header>
            );
          })()}

          <section className="grid grid-cols-4 gap-2 text-[11px] mt-4 py-3 border-b border-slate-200">
            <div><span className="uppercase text-slate-500">Patient:</span> <span className="font-medium">{patient.name}</span></div>
            <div><span className="uppercase text-slate-500">Age/Sex:</span> {patient.age} / {patient.sex}</div>
            <div><span className="uppercase text-slate-500">ID:</span> <span className="tabular">{patient.patient_id}</span></div>
            <div><span className="uppercase text-slate-500">Date:</span> <span className="tabular">{new Date(rx.created_at).toLocaleDateString()}</span></div>
            <div><span className="uppercase text-slate-500">Mobile:</span> {patient.mobile}</div>
            <div><span className="uppercase text-slate-500">Visit:</span> <span className="tabular">{rx.visit_id}</span></div>
            <div className="col-span-2"><span className="uppercase text-slate-500">Referred by:</span> {patient.referred_by || "—"}</div>
          </section>

          {visit && (
            <>
              {(() => {
                // Vitals-missing reminder — SCREEN ONLY. Warn if any core
                // measurement is blank so the doctor can go back and fill it
                // before printing. Non-blocking; the button to print is
                // still available.
                const v = visit.vitals || {};
                const isBlank = (x) => x === null || x === undefined || String(x).trim() === "";
                const missing = [];
                if (isBlank(v.weight)) missing.push("Weight");
                if (isBlank(v.height)) missing.push("Height");
                if (isBlank(v.bmi)) missing.push("BMI");
                if (isBlank(v.waist)) missing.push("Waist");
                if (isBlank(v.sbp) || isBlank(v.dbp)) missing.push("BP");
                if (isBlank(v.pulse)) missing.push("HR");
                if (!missing.length) return null;
                return (
                  <div className="no-print py-2 border-b border-amber-200 bg-amber-50 -mx-6 px-6 flex items-start gap-2" data-testid="vitals-missing-banner">
                    <span className="text-[11px] font-semibold text-amber-800 uppercase tracking-wider">Missing vitals:</span>
                    <span className="text-[11px] text-amber-900 flex-1">
                      {missing.join(" · ")} — consider going back to the visit and filling these before printing.
                    </span>
                  </div>
                );
              })()}
              <section className="grid grid-cols-9 gap-2 text-[11px] py-3 border-b border-slate-200 tabular">
                <div><div className="text-slate-500 uppercase">BP</div>{visit.vitals?.sbp || "-"}/{visit.vitals?.dbp || "-"}</div>
                <div><div className="text-slate-500 uppercase">Pulse</div>{visit.vitals?.pulse || "-"}</div>
                <div><div className="text-slate-500 uppercase">Weight</div>{visit.vitals?.weight || "-"}<span className="text-slate-400 text-[9px] ml-0.5">kg</span></div>
                <div><div className="text-slate-500 uppercase">Height</div>{visit.vitals?.height || "-"}<span className="text-slate-400 text-[9px] ml-0.5">cm</span></div>
                <div><div className="text-slate-500 uppercase">BMI</div>{visit.vitals?.bmi || "-"}</div>
                <div><div className="text-slate-500 uppercase">Waist</div>{visit.vitals?.waist || "-"}<span className="text-slate-400 text-[9px] ml-0.5">cm</span></div>
                <div><div className="text-slate-500 uppercase">SpO₂</div>{visit.vitals?.spo2 || "-"}</div>
                <div><div className="text-slate-500 uppercase">RBS</div>{visit.vitals?.random_sugar || "-"}</div>
                <div><div className="text-slate-500 uppercase">Temp</div>{visit.vitals?.temperature || "-"}</div>
              </section>
            </>
          )}

          {(() => {
            // Calculated indices strip — surfaces eGFR, ACR, FIB-4, HOMA-IR
            // and other derived values recorded in the investigations table.
            // Also includes any indice the doctor has explicitly PINNED via
            // the ClinicalCalculators panel (persisted in localStorage).
            const invs = visit?.investigations || [];
            const idxPatterns = [
              { key: "eGFR", rx: /egfr/i },
              { key: "ACR", rx: /\bacr\b|albumin.creat|uacr/i },
              { key: "FIB-4", rx: /fib.?4/i },
              { key: "HOMA-IR", rx: /homa.ir/i },
              { key: "HOMA-β", rx: /homa.?[b\u03b2]/i },
              { key: "Non-HDL", rx: /non.?hdl/i },
              { key: "TC/HDL", rx: /tc.?\/?.?hdl|tc.hdl ratio/i },
              { key: "TG/HDL", rx: /tg.?\/?.?hdl|tg.hdl ratio/i },
              { key: "BSA", rx: /^bsa|\bbsa\b/i },
              { key: "eAG", rx: /^eag\b|estimated average glucose/i },
              { key: "Corr. Na", rx: /corrected sodium|corr.*na/i },
              { key: "Corr. Ca", rx: /corrected calcium|corr.*ca/i },
            ];
            const picks = [];
            const seen = new Set();
            idxPatterns.forEach(({ key, rx }) => {
              const hit = invs.find((i) => rx.test(i.test_name || ""));
              // MT-11B.8: a blank result is never printed as a unit-only row
              if (hit && !seen.has(key) && hit.result !== null && hit.result !== undefined && String(hit.result).trim() !== "") {
                picks.push({ key, value: hit.result, unit: hit.unit, interp: "", hint: "", pinned: false });
                seen.add(key);
              }
            });
            // Merge in pinned calculator values. Preferred source is the
            // visit itself (persisted on save so re-prints and same-date
            // regenerations show the same pins). Fall back to localStorage
            // for old visits saved before `visit.pinned_calculators` existed.
            let pinned = Array.isArray(visit?.pinned_calculators) ? visit.pinned_calculators : null;
            if (!pinned) {
              try { pinned = JSON.parse(localStorage.getItem("print_pinned_calcs_v1") || "[]"); } catch (e) { console.debug("[silent]", e); }
            }
            (pinned || []).forEach((p) => {
              if (p.value === null || p.value === undefined || p.value === "") return;
              if (!seen.has(p.title)) {
                picks.push({ key: p.title, value: p.value, unit: p.unit, interp: p.interp || "", hint: p.hint || "", pinned: true });
                seen.add(p.title);
              }
            });
            if (picks.length === 0) return null;
            // If any pinned entry has interpretation/hint text, render a
            // richer 2-column table so the Significance column can show
            // clinical reasoning (screen only — hidden from the print
            // via `no-print`). Otherwise keep the compact 6-across strip.
            const hasSignificance = picks.some((p) => p.interp || p.hint);
            if (!hasSignificance) {
              return (
                <section className="py-3 border-b border-slate-200" data-testid="print-calc-indices">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1.5">Calculated indices</div>
                  <div className="grid grid-cols-6 gap-2 text-[11px] tabular">
                    {picks.map((p) => (
                      <div key={p.key} data-testid={`print-indice-${p.key.replace(/\s+/g,"-").toLowerCase()}`}>
                        <div className="text-slate-500 uppercase text-[9px]">{p.key}{p.pinned && <span className="ml-0.5 text-pink-600">•</span>}</div>
                        <div>{p.value}{p.unit ? ` ${p.unit}` : ""}</div>
                      </div>
                    ))}
                  </div>
                </section>
              );
            }
            return (
              <section className="py-3 border-b border-slate-200" data-testid="print-calc-indices">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1.5">Calculated indices</div>
                <table className="w-full text-[11px] tabular">
                  <thead>
                    <tr className="text-left text-slate-500 border-b border-slate-100">
                      <th className="py-1 font-medium w-40">Metric</th>
                      <th className="py-1 font-medium w-28">Value</th>
                      <th className="py-1 font-medium no-print" data-testid="print-calc-sig-header">
                        Significance <span className="text-[9px] text-slate-400 normal-case">(doctor&rsquo;s reference — hidden on print)</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {picks.map((p) => (
                      <tr key={p.key} className="border-b border-slate-50 last:border-b-0" data-testid={`print-indice-${p.key.replace(/\s+/g,"-").toLowerCase()}`}>
                        <td className="py-1 text-slate-700">
                          {p.key}{p.pinned && <span className="ml-1 text-pink-600" title="Pinned by doctor">•</span>}
                        </td>
                        <td className="py-1">{p.value}{p.unit ? ` ${p.unit}` : ""}</td>
                        <td className="py-1 text-slate-600 no-print">
                          {p.interp && <span className="italic text-slate-700">{p.interp}</span>}
                          {p.interp && p.hint && <span className="mx-1 text-slate-400">·</span>}
                          {p.hint && <span>{p.hint}</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </section>
            );
          })()}

          <section className="py-3 border-b border-slate-200">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Diagnosis</div>
            <div className="text-sm mt-1">
              <span className="font-medium">{rx.diagnosis?.primary}</span>
              {rx.diagnosis?.secondary && <span className="text-slate-600"> · {rx.diagnosis.secondary}</span>}
              {rx.diagnosis?.icd10 && <span className="text-slate-500 ml-2 tabular text-xs">[{rx.diagnosis.icd10}]</span>}
            </div>
            {visit?.known_case_of?.length > 0 && (
              <div className="text-[11px] text-slate-600 mt-1">Known: {visit.known_case_of.join(", ")}</div>
            )}
            {visit?.allergy_flags?.length > 0 && (
              <div className="text-[11px] text-red-700 mt-1"><strong>Alerts:</strong> {visit.allergy_flags.join(", ")}</div>
            )}
          </section>

          <section className="py-3">
            <div className="flex items-center gap-2 mb-2 rx-section-heading">
              <div className="font-serif italic text-2xl">℞</div>
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Medicines</div>
            </div>
            <table className="w-full text-[11px] border border-slate-300 rx-med-table">
              <thead className="bg-slate-100">
                <tr>
                  <th className="p-1.5 border-b border-slate-300 text-left w-8">#</th>
                  <th className="p-1.5 border-b border-slate-300 text-left">Medicine</th>
                  <th className="p-1.5 border-b border-slate-300 text-left">Dose / Route</th>
                  <th className="p-1.5 border-b border-slate-300 text-left">Frequency</th>
                  <th className="p-1.5 border-b border-slate-300 text-left">Duration</th>
                  <th className="p-1.5 border-b border-slate-300 text-left w-12">Qty</th>
                </tr>
              </thead>
              <tbody>
                {(rx.medicines || []).map((m, i) => {
                  const freqT = tr(m.frequency, lang, customDict);
                  const timingT = tr(m.timing, lang, customDict);
                  const routeT = tr(m.route, lang, customDict);
                  const instrT = tr(m.instructions, lang, customDict);
                  const hasTrans = lang !== "en" && (freqT || timingT || routeT || instrT);
                  return (
                  <tr key={m._uid || `pmed-${i}`} className={`border-b border-slate-200 align-top ${m.type === "insulin" ? "bg-amber-50/50" : ""}`}>
                    <td className="p-1.5 tabular">{i + 1}</td>
                    <td className="p-1.5">
                      <div className="font-medium">
                        {m.type === "insulin" && <span className="mr-1 text-[9px] uppercase tracking-wider bg-amber-500 text-white px-1 py-0.5 rounded">Insulin</span>}
                        {(() => {
                          const bn = String(m.brand_name || "");
                          // MT-11B.7: only a PRODUCT strength joins the title —
                          // never a patient dose that was stored as strength.
                          const st = productStrengthLabel(m);
                          if (!st) return bn;
                          const bnNorm = bn.toLowerCase().replace(/\s+/g, "");
                          const stNorm = st.toLowerCase().replace(/\s+/g, "");
                          if (bnNorm.includes(stNorm)) return bn;
                          // numeric comparison: "Metafort 500 PR" + "500 mg" / "Telma 20" + "20mg"
                          const stNums = st.match(/\d+(?:\.\d+)?/g) || [];
                          const bnNums = new Set(bn.match(/\d+(?:\.\d+)?/g) || []);
                          if (stNums.length && stNums.every((n) => bnNums.has(n))) return bn;
                          return `${bn} ${st}`;
                        })()}
                      </div>
                      <div className="text-[10px] text-slate-500">{m.generic_name}</div>
                      {isCombinedProductName(m.brand_name) && !rx.quality_version && (
                        <div className="no-print text-[9px] text-amber-700 italic" data-testid={`print-historical-unverified-${i}`}>Unverified product — historical (reproduced as originally recorded)</div>
                      )}
                      {m.type === "insulin" && m.insulin_regimen && (
                        <div className="text-[10px] text-slate-700 mt-0.5 tabular">
                          {["morning", "afternoon", "evening", "bedtime"].filter((k) => m.insulin_regimen[k]).map((k) => `${k[0].toUpperCase() + k.slice(1)} ${m.insulin_regimen[k]}U`).join(" · ")}
                          {m.insulin_regimen.site && <span className="text-slate-500"> · Site: {m.insulin_regimen.site}</span>}
                        </div>
                      )}
                      {m.instructions && printTitrate && <div className="text-[10px] text-slate-600 italic">{m.instructions}</div>}
                      {hasTrans && instrT && printTitrate && <div className="text-[10px] text-pink-700 italic">{instrT}</div>}
                    </td>
                    <td className="p-1.5">
                      <div data-testid={`print-dose-${i}`}>{doseDisplay(m)} · {m.route}</div>
                      {hasTrans && routeT && <div className="text-[10px] text-pink-700">{routeT}</div>}
                    </td>
                    <td className="p-1.5">
                      <div>{m.frequency} {m.timing ? `(${m.timing})` : ""}</div>
                      {hasTrans && (freqT || timingT) && (
                        <div className="text-[10px] text-pink-700">{freqT}{timingT ? ` (${timingT})` : ""}</div>
                      )}
                    </td>
                    <td className="p-1.5">{m.duration}</td>
                    <td className="p-1.5 tabular">{m.quantity}{m.type === "insulin" ? " cart." : ""}</td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
            {printCost && costEst && costEst.total > 0 && (
              <div className="mt-1.5 flex items-baseline justify-end gap-1.5" data-testid="rx-cost-total">
                <span className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Estimated medicine cost</span>
                <span className="text-[13px] font-bold text-emerald-800 tabular">₹{costEst.total.toFixed(0)}</span>
                <span className="text-[9px] text-slate-400">
                  ({costEst.priced}/{costEst.count} items · clinic price list · whole packs)
                </span>
              </div>
            )}
          </section>

          {(visit?.advice || rx.advice) && (
            <section className="py-3 border-t border-slate-200">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Advice</div>
              <div className="text-[11px] mt-1 whitespace-pre-line" data-testid="advice-text">{stripMarkdown(visit?.advice || rx.advice)}</div>
              {lang !== "en" && (
                <div lang={lang} dir={languageDirection(lang)} className="text-[11px] mt-1 whitespace-pre-line text-pink-700 italic" data-testid="advice-translated">
                  {stripMarkdown(adviceTranslation.translated)}
                </div>
              )}
            </section>
          )}

          {visit?.next_investigations && (
            <section className="py-3 border-t border-slate-200">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Next investigations</div>
              <div className="text-[11px] mt-1">{visit.next_investigations}</div>
            </section>
          )}

          {(() => {
            const rev = Array.isArray(visit?.imaging_reviewed) ? visit.imaging_reviewed.filter((r) => r && r.study) : [];
            if (!rev.length) return null;
            const fmtRd = (d) => {
              const dt = new Date(d);
              return isNaN(dt.getTime()) ? d : dt.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
            };
            return (
              <section className="py-2 border-t border-slate-200" data-testid="print-reports-reviewed">
                <div className="text-[10px] text-slate-600 leading-snug">
                  <span className="uppercase tracking-wider text-slate-500 font-semibold">Reports reviewed:</span>{" "}
                  {rev.map((r) => `${r.study}${r.report_date ? ` (${fmtRd(r.report_date)})` : ""}${r.summary ? ` — ${r.summary}` : ""}`).join("; ")} ✓
                </div>
              </section>
            );
          })()}

          {(() => {
            const fh = activeFhInsights(visit?.clinical_history?.family_hx, patient?.screenings_done);
            if (!fh.length) return null;
            return (
              <section className="py-3 border-t border-slate-200" data-testid="print-fh-screening">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Family history — screening advised</div>
                {fh.map((f) => (
                  <div key={f.id} className="text-[10px] mt-0.5 text-slate-700 leading-snug">
                    • <span className="font-semibold">{f.name}:</span> {f.actions}
                  </div>
                ))}
              </section>
            );
          })()}

          {Array.isArray(visit?.vaccines_planned || rx?.vaccines_planned) && ((visit?.vaccines_planned || rx?.vaccines_planned || []).length > 0) && (
            <section className="py-3 border-t border-slate-200" data-testid="print-vaccines-planned">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Vaccines scheduled</div>
              <table className="w-full text-[11px] mt-1">
                <thead>
                  <tr className="text-left text-slate-500 border-b border-slate-100">
                    <th className="py-1 font-medium">Vaccine</th>
                    <th className="py-1 font-medium w-40">Appointment date</th>
                    <th className="py-1 font-medium w-56 no-print">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {(visit?.vaccines_planned || rx?.vaccines_planned || []).map((v, i) => {
                    const key = `${v.name}__${v.date}`;
                    const busy = !!cancellingRecall[key];
                    const recallOn = hasRecall(v);
                    return (
                    <tr key={i} className="border-b border-slate-50" data-testid={`vaccine-row-${i}`}>
                      <td className="py-1">{v.name || "—"}</td>
                      <td className="py-1 tabular">{v.date ? new Date(v.date).toLocaleDateString("en-IN", {day:"2-digit",month:"short",year:"numeric"}) : "—"}</td>
                      <td className="py-1 no-print">
                        <div className="flex items-center gap-1.5">
                          <button
                            type="button"
                            onClick={() => sendVaccineWhatsApp(v)}
                            disabled={!v.name || !patient?.mobile}
                            data-testid={`vaccine-whatsapp-${i}`}
                            title={patient?.mobile ? `WhatsApp ${patient?.name || "patient"}` : "Patient mobile not on file"}
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 disabled:opacity-50 disabled:cursor-not-allowed text-[10px]"
                          >
                            <MessageCircle size={11} /> WhatsApp
                          </button>
                          {recallOn && (
                            <button
                              type="button"
                              onClick={() => cancelRecall(v)}
                              disabled={busy}
                              data-testid={`vaccine-cancel-recall-${i}`}
                              title="Cancel the T-2 Google Calendar recall for this vaccine"
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-rose-300 bg-rose-50 text-rose-700 hover:bg-rose-100 disabled:opacity-50 disabled:cursor-not-allowed text-[10px]"
                            >
                              {busy ? <Loader2 size={11} className="animate-spin" /> : <BellOff size={11} />}
                              Cancel recall
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </section>
          )}

          {visit?.next_steps && (
            <section className="py-3 border-t border-slate-200 rx-next-steps-block" data-testid="print-next-steps">
              <div className="flex items-center gap-2 mb-2">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Patient handout (Next Steps)</div>
                {visit.next_steps.handout_lang && (
                  <span className="text-[9px] uppercase tracking-wider bg-pink-100 text-pink-800 px-1.5 py-0.5 rounded font-semibold">
                    {visit.next_steps.handout_lang}
                  </span>
                )}
              </div>
              {visit.next_steps.summary && (
                <div className="text-[11px] italic text-slate-600 mb-2">{visit.next_steps.summary}</div>
              )}
              {visit.next_steps.next_followup && (canonicalFollowUpLabel || visit.next_steps.next_followup.reason) && (
                <div className="text-[11px] mb-2" data-testid="print-next-followup">
                  <span className="text-slate-500 uppercase text-[9px] tracking-wider">Follow-up</span>{" "}
                  {/* MT-11B.7: ONE canonical date — the visit's saved follow_up_date, never the raw AI suggestion */}
                  <span className="font-medium">{canonicalFollowUpLabel || rx.follow_up || "—"}</span>
                  {visit.next_steps.next_followup.reason && <span className="text-slate-600"> — {visit.next_steps.next_followup.reason}</span>}
                </div>
              )}
              {Array.isArray(visit.next_steps.next_vaccines) && visit.next_steps.next_vaccines.length > 0 && (
                <div className="text-[11px] mb-2" data-testid="print-next-vaccines">
                  <div className="text-slate-500 uppercase text-[9px] tracking-wider mb-0.5">Next vaccines</div>
                  <ul className="list-disc pl-5 space-y-0.5">
                    {visit.next_steps.next_vaccines.map((v, i) => (
                      <li key={`vax-${v.name || i}`}>
                        <span className="font-medium">{v.name}</span>
                        {v.due && <span className="text-slate-600"> — {v.due}</span>}
                        {v.why && <span className="text-slate-500"> ({v.why})</span>}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {Array.isArray(visit.next_steps.next_labs) && visit.next_steps.next_labs.length > 0 && (
                <div className="text-[11px] mb-2" data-testid="print-next-labs">
                  <div className="text-slate-500 uppercase text-[9px] tracking-wider mb-0.5">Next lab tests</div>
                  <ul className="list-disc pl-5 space-y-0.5">
                    {visit.next_steps.next_labs.map((l, i) => (
                      <li key={`lab-${l.lab || i}`}>
                        <span className="font-medium">{l.lab}</span>
                        {l.when && <span className="text-slate-600"> — {l.when}</span>}
                        {l.reason && <span className="text-slate-500"> ({l.reason})</span>}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {Array.isArray(visit.next_steps.patient_education) && visit.next_steps.patient_education.length > 0 && (
                <div className="text-[11px]" data-testid="print-patient-education">
                  <div className="text-slate-500 uppercase text-[9px] tracking-wider mb-0.5">Patient education</div>
                  <ul className="list-disc pl-5 space-y-0.5">
                    {visit.next_steps.patient_education.map((e, i) => (
                      <li key={`edu-${e.topic || i}`}>
                        <span className="font-medium">{e.topic}:</span> <span className="text-slate-700">{e.text || e.text_en}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </section>
          )}

          {/* Doctor-ticked patient education handouts */}
          {Array.isArray(visit?.education_ids) && visit.education_ids.length > 0 && (
            <section className="py-3 border-t border-slate-200" data-testid="print-education-handouts">
              <div className="flex items-center gap-2 mb-2">
                <div className="text-[10px] uppercase tracking-wider text-pink-700 font-semibold">For the patient</div>
                <div className="text-[9px] text-slate-500 italic">— please read carefully and follow the advice below</div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {visit.education_ids.map((eid) => {
                  const educationLang = visit.education_language || patient?.preferred_language || "en";
                  const e = getEducationText(eid, educationLang);
                  if (!e) return <div key={eid} role="status" data-testid={`print-edu-unavailable-${eid}`} className="text-xs text-amber-800">Handout unavailable in {patientLanguageName(educationLang)} — no translated handout attached.</div>;
                  return (
                    <div key={eid} className="border border-slate-200 rounded-md p-2" data-testid={`print-edu-${eid}`}>
                      <div className="text-[11px] font-semibold text-slate-800">{e.title}</div>
                      <div className="text-[10px] text-slate-700 mt-0.5 leading-snug">{e.body}</div>
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          <section className="py-3 border-t border-slate-200 flex justify-between items-end rx-signature-block">
            <div className="flex items-start gap-4">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Follow-up</div>
                <div className="text-sm font-medium" data-testid="print-follow-up">
                  {rx.follow_up || "-"}
                  {canonicalFollowUpLabel && <span className="text-slate-600 font-normal"> · {canonicalFollowUpLabel}</span>}
                </div>
                {rx.safety_reviewed && (
                  <div className="text-[8.5px] text-slate-500 mt-1.5 leading-snug max-w-[230px]" data-testid="rx-safety-reviewed">
                    ✓ Drug interactions &amp; safety checks reviewed electronically
                    {(rx.safety_overrides?.length || rx.override_reason) && (
                      <span> · clinical override{rx.safety_overrides?.length > 1 ? "s" : ""} documented on file</span>
                    )}
                  </div>
                )}
              </div>
              {rx.verify_token && (
                <div className="flex flex-col items-center pl-4 border-l border-slate-200" data-testid="rx-qr-block">
                  <QRCodeSVG
                    value={verifyRxUrl(rx)}
                    size={72}
                    level="M"
                    marginSize={0}
                  />
                  <div className="text-[8px] uppercase tracking-wider text-slate-500 mt-1 font-semibold">Verify Rx</div>
                  <div className="text-[7.5px] text-slate-400 leading-tight text-center">Scan to authenticate</div>
                </div>
              )}
              {printKitQr && careLink && (
                <div className="flex flex-col items-center pl-4 border-l border-slate-200" data-testid="rx-kit-qr-block">
                  <QRCodeSVG
                    value={withLang(careLink, lang)}
                    size={72}
                    level="M"
                    marginSize={0}
                  />
                  <div className="text-[8px] uppercase tracking-wider text-pink-700 mt-1 font-semibold">Your Care Kit</div>
                  <div className="text-[7.5px] text-slate-400 leading-tight text-center max-w-[92px]">Scan to reopen your prescription &amp; care kit at home</div>
                </div>
              )}
            </div>
            <div className="text-right max-w-[62%]">
              {(() => {
                const doc = resolveDoctor(rx?.signed_by_doctor_id);
                return (
                  <>
                    {doc.signature_url && (
                      <div className="flex justify-end mb-1" data-testid="rx-doctor-signature">
                        <img
                          src={absUrl(doc.signature_url)}
                          alt={`Signature — ${doc.name}`}
                          className="h-12 max-w-[220px] object-contain"
                        />
                      </div>
                    )}
                    <div className="border-t border-slate-800 pt-1 mt-8 text-[11px]">
                      <div className="font-medium">{doctorDisplay(doc.name)}</div>
                      <div className="text-[9.5px] text-slate-600 leading-snug mt-0.5" data-testid="rx-doctor-qualifications">
                        {doc.qualifications}
                      </div>
                      {doc.regNo && (
                        <div className="text-[9px] text-slate-500 mt-0.5" data-testid="rx-doctor-regno">
                          {doc.regNo}
                        </div>
                      )}
                    </div>
                  </>
                );
              })()}
            </div>
          </section>

          <footer className="mt-4 pt-3 border-t border-slate-200 text-[9px] text-slate-500 leading-relaxed print-doctor-footer">
            {(() => {
              const doc = resolveDoctor(rx?.signed_by_doctor_id);
              const credsShort = doc.qualifications || "";
              return (
                <>This prescription is issued by <strong>{doctorDisplay(doc.name)}{credsShort ? ` (${credsShort})` : ""}</strong>, {CLINIC.name}. Every medicine is reviewed and verified. All clinical decisions remain solely with {doctorDisplay(doc.name)}.</>
              );
            })()}
            {!rx.quality_version && (
              <div className="mt-1 text-slate-400" data-testid="print-historical-reprint-footer">Historical reprint — content reproduced as originally recorded · reprinted {new Date().toISOString().slice(0, 16).replace("T", " ")} UTC</div>
            )}
          </footer>

          {/* Legal expiry / dispensing disclaimer — small yet bold. Printed
              on every A4 Rx page for regulatory clarity (see clinical
              governance note). Uses the same language as the print
              language switcher so the patient reads it in their own tongue. */}
          <p className="mt-2 text-[9px] font-bold text-slate-700 leading-snug print-rx-disclaimer" data-testid="rx-legal-disclaimer" lang={hasPrintDictionary ? lang : "en"}>
            {rxLegalDisclaimer(lang)}
          </p>
        </div>

        {/* Clinic footer strip — 3-column elegant strip. Repeats at bottom of
            every printed A4 page via position:fixed inside @media print.
            Shown on-screen so the doctor knows what the patient sees.
            MT-11C.4: every clinic-specific line comes from the ACTIVE tenant's
            profile (specialties / timings / phones / WhatsApp); nothing hardcoded. */}
        <div className="print-marketing-footer border-t-2 border-pink-600 bg-gradient-to-r from-pink-50 via-white to-pink-50 text-slate-700" data-testid="print-marketing-footer">
          <div className={`grid ${CLINIC.specialties ? "grid-cols-3" : "grid-cols-2"} gap-3 px-4 py-2`}>
            {CLINIC.specialties && (
              <div className="border-r border-slate-300 pr-3">
                <div className="text-[9px] uppercase tracking-wider text-pink-700 font-bold mb-0.5">Our Services</div>
                <div className="text-[8.5px] leading-tight text-slate-700" data-testid="print-footer-services">{CLINIC.specialties}</div>
              </div>
            )}
            <div className="border-r border-slate-300 pr-3">
              <div className="text-[9px] uppercase tracking-wider text-pink-700 font-bold mb-0.5">Evidence-Based Prescribing</div>
              <div className="text-[8.5px] leading-tight text-slate-700">
                Every medicine here is selected using the latest KDIGO, ADA, AHA/ACC and ESC guidelines. Doses are calibrated to the patient&rsquo;s weight, eGFR and comorbidities.
              </div>
            </div>
            <div>
              <div className="text-[9px] uppercase tracking-wider text-pink-700 font-bold mb-0.5">Consult with Confidence</div>
              <div className="text-[8.5px] leading-tight text-slate-700" data-testid="print-footer-contact">
                {CLINIC.timings && <>Hours: {CLINIC.timings}<br /></>}
                {(CLINIC.phones || []).length > 0 && <>Phone: {CLINIC.phones.join(" · ")}<br /></>}
                {CLINIC.services_whatsapp && <>WhatsApp: {CLINIC.services_whatsapp}<br /></>}
                Emergency: contact the clinic reception first
              </div>
            </div>
          </div>
          {CLINIC.footer && (
            <div className="mt-2 pt-2 border-t border-slate-200 text-[9px] italic text-slate-600 text-center" data-testid="print-clinic-footer">
              {CLINIC.footer}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
